import React from 'react';
import { Button, Popconfirm, Table, Tag } from 'antd';
import { PauseCircleOutlined, SyncOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { SmartBetaStatus } from '../../../services/smartBetaApi';
import { formatNumber } from '../../../utils/numberFormat';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { Card, CardTitle, Muted, Row } from '../styles';

interface Props {
  status: SmartBetaStatus | null;
  nextRebalance: string | null;
  /** Unsaved edits or no capital: a rebalance would trade on something else. */
  startBlockedReason: string | null;
  busy: boolean;
  onStart: () => void;
  onStop: () => void;
}

/** The worker reports thousand VND. */
const vnd = (thousand?: string | number | null) =>
  thousand == null ? '—' : formatNumber(Math.round(Number(thousand) * 1000));

const PHASE_LABEL: Record<string, string> = {
  waiting: 'Waiting for the session',
  sell: 'Selling',
  buy: 'Buying',
  done: 'Done',
  stopped: 'Stopped before finishing',
};

export function stateOf(status: SmartBetaStatus | null): { label: string; color: string; running: boolean } {
  const exec = status?.execution?.status;
  if (exec === 'STARTING') return { label: 'Starting', color: 'processing', running: true };
  if (exec === 'STOPPING') return { label: 'Stopping', color: 'warning', running: true };
  if (exec === 'PASSED') {
    const phase = status?.progress?.phase;
    return { label: phase ? (PHASE_LABEL[phase] ?? phase) : 'Running', color: 'processing', running: true };
  }
  if (status?.progress?.phase === 'done') {
    return { label: `Rebalanced ${status.progress.day}`, color: 'success', running: false };
  }
  return { label: 'Not running', color: 'default', running: false };
}

const RebalancePanel: React.FC<Props> = ({ status, nextRebalance, startBlockedReason, busy, onStart, onStop }) => {
  const isMobile = useIsMobile();
  const state = stateOf(status);
  const progress = status?.progress;
  const plan = progress?.plan;
  const symbols = plan
    ? [...new Set([...Object.keys(plan.target), ...Object.keys(plan.sell), ...Object.keys(plan.buy)])].sort()
    : [];

  return (
    <Card aria-label="Rebalance">
      <CardTitle>
        <SyncOutlined spin={state.running && status?.execution?.status === 'PASSED'} /> Rebalance{' '}
        <Tag color={state.color}>{state.label}</Tag>
      </CardTitle>
      <Row>
        <Muted>
          {status?.rebalanceToday
            ? 'Today is a rebalance day — the bot starts by itself at 09:20.'
            : 'Next rebalance: '}
          {!status?.rebalanceToday && (nextRebalance ?? 'not scheduled')}
          {!status?.rebalanceToday && nextRebalance && ' (moved to the next trading day if the market is closed)'}
        </Muted>
      </Row>
      <Row>
        {state.running ? (
          <Popconfirm
            title="Stop the rebalance?"
            description="Unfilled orders are cancelled. What already filled stays."
            onConfirm={onStop}
            okText="Stop"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              icon={<PauseCircleOutlined />}
              loading={busy}
              disabled={status?.execution?.status === 'STOPPING'}
            >
              Stop
            </Button>
          </Popconfirm>
        ) : (
          <Popconfirm
            title="Rebalance now?"
            description="The bot sells what is overweight, then buys what is underweight, with real orders, until 14:25."
            onConfirm={onStart}
            okText="Rebalance"
            disabled={!!startBlockedReason}
          >
            <Button type="primary" icon={<ThunderboltOutlined />} loading={busy} disabled={!!startBlockedReason}>
              Rebalance now
            </Button>
          </Popconfirm>
        )}
        {startBlockedReason && !state.running && <Muted>{startBlockedReason}</Muted>}
      </Row>

      {plan && (
        <>
          <Row>
            <Muted>
              Plan of {progress!.day} on {vnd(plan.capital)} VND capital
              {progress!.updated_at ? ` · updated ${new Date(progress!.updated_at).toLocaleTimeString('en-GB')}` : ''}
            </Muted>
          </Row>
          <Table
            size="small"
            rowKey="symbol"
            pagination={false}
            scroll={{ x: true }}
            dataSource={symbols.map((symbol) => ({ symbol }))}
            columns={[
              { title: 'Ticker', dataIndex: 'symbol' },
              { title: 'Target (VND)', align: 'right', render: (_, r) => vnd(plan.target[r.symbol] ?? 0) },
              {
                title: 'Sell (shares)',
                align: 'right',
                render: (_, r) =>
                  plan.sell[r.symbol]
                    ? `${formatNumber(plan.sell[r.symbol] - (progress!.remaining_sell[r.symbol] ?? 0))} / ${formatNumber(plan.sell[r.symbol])}`
                    : '—',
              },
              ...(isMobile
                ? []
                : [
                    {
                      title: 'Buy left (VND)',
                      align: 'right' as const,
                      render: (_: unknown, r: { symbol: string }) =>
                        plan.buy[r.symbol] ? vnd(progress!.remaining_buy[r.symbol] ?? plan.buy[r.symbol]) : '—',
                    },
                  ]),
              {
                title: 'Matched',
                align: 'right',
                render: (_, r) => formatNumber(progress!.matched[r.symbol] ?? 0),
              },
            ]}
          />
        </>
      )}
    </Card>
  );
};

export default RebalancePanel;
